import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { PropertyInfoPanel } from './PropertyInfoPanel';

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

// Address slot group — mirrors the Footer's address pattern so the visit-us
// card becomes a link to the property's Google Place page when set. The
// schema literal references this constant (extractor follows one level).
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, City, ST 00000',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at
    // render time inside PropertyInfoPanel. Editor overrides still win.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  const prefix = 'contactform';
  const formBlockPath = `${prefix}.form_block`;

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-lead-form',
  });

  return (
    <>
      {/* Secondary-color section: form alongside a single column of info cards.
          On mobile the cards stack beneath the form (DOM order + column flex). */}
      <div className="cf-section">
        <div className="cf-container">
          <div className="cf-layout">
            {/* Contact form */}
            <div className="cf-form-wrap">
              <EditableToken path={formBlockPath} as="div">
                {formSlot ? (
                  <SectionContextProvider sectionId={formSlot.sectionId}>
                    <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                  </SectionContextProvider>
                ) : (
                  <div className="cf-form-empty-card">
                    <p className="cf-form-empty">No form block selected</p>
                  </div>
                )}
              </EditableToken>
            </div>

            {/* Visit Us / Call Us / Office Hours — stacked in a single column */}
            <div className="cf-info-col">
              <PropertyInfoPanel prefix={prefix} addressDefinition={addressLinkDef} />
            </div>
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — static paths so the linter can match each schema key.
          The live EditableTokens in PropertyInfoPanel use dynamic paths from prefix. */}
      <EditableToken path="contactform.form_block"   as="div"  className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.visit_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableSlotGroup
        groupPath="contactform.address"
        definition={addressLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableToken path="contactform.call_label"   as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.phone"        as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="contactform.hours_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        /* ── Section: solid secondary-color background ── */
        .cf-section {
          background: var(--vibe-color-secondary, #3E441D);
          padding: 2rem 1.5rem;
        }
        @media (min-width: 768px) {
          .cf-section { padding: 4rem 2rem; }
        }
        .cf-container {
          max-width: 72rem;
          margin: 0 auto;
        }

        /* Mobile: single column — form first, info cards beneath.
           Wider screens: form beside a single column of info cards. */
        .cf-layout {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        @media (min-width: 768px) {
          .cf-layout {
            flex-direction: row;
            align-items: flex-start;
            gap: 2rem;
          }
        }

        .cf-form-wrap {
          width: 100%;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
        }
        @media (min-width: 768px) {
          .cf-form-wrap { flex: 1 1 60%; min-width: 0; }
        }
        .cf-form-empty-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }

        /* Info column — sits beside the form on wider screens */
        .cf-info-col {
          width: 100%;
        }
        @media (min-width: 768px) {
          .cf-info-col { flex: 1 1 40%; max-width: 24rem; }
        }

        /* Stack the Visit Us / Call Us / Office Hours cards vertically.
           Scoped to this layout so the shared PropertyInfoPanel keeps its
           default (horizontal) styling elsewhere (e.g. ScheduleTourForm). */
        .cf-info-col .cf-info-cards {
          flex-direction: column;
          flex-wrap: nowrap;
          gap: 1rem;
        }
        .cf-info-col .cf-card {
          flex: 0 0 auto;
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
    'contactform.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'contactform.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'contactform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'contactform.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'contactform.address': addressLinkDef,
  },
  collectionBindings: {
    'contactform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
